import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Activity, Server, Database, ListTree, Bug, ShieldAlert } from "lucide-react";

function Stat({ label, value, suffix }) {
  return (
    <div className="surface-card p-4">
      <div className="label-overline">{label}</div>
      <div className="font-display text-2xl font-black mt-2">{value ?? "—"}{suffix && <span className="text-sm text-muted-foreground ml-1">{suffix}</span>}</div>
    </div>
  );
}

export default function Monitoring() {
  const [snap, setSnap] = useState(null);
  useEffect(() => {
    const load = () => api.get("/admin/monitoring/snapshot").then(({ data }) => setSnap(data));
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="admin-monitoring">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-black">Platform monitoring</h1>
        <p className="text-muted-foreground">API performance, server health, database, queues, errors and security alerts. Refreshes every 15s.</p>
      </div>

      <Card><CardHeader className="flex flex-row items-center gap-2"><Activity className="w-4 h-4" /><CardTitle>API performance</CardTitle></CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-4 gap-3">
            <Stat label="Calls (last hour)" value={snap?.api?.calls_last_hour} />
            <Stat label="p95 latency" value={snap?.api?.p95_ms} suffix="ms" />
            <Stat label="p99 latency" value={snap?.api?.p99_ms} suffix="ms" />
            <Stat label="Error rate" value={snap?.api?.error_rate} suffix="%" />
          </div>
        </CardContent></Card>

      <Card><CardHeader className="flex flex-row items-center gap-2"><Server className="w-4 h-4" /><CardTitle>Server health</CardTitle></CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-4 gap-3">
            <Stat label="CPU" value={snap?.server?.cpu} suffix="%" />
            <Stat label="Memory" value={snap?.server?.memory} suffix="%" />
            <Stat label="Disk" value={snap?.server?.disk} suffix="%" />
            <Stat label="Uptime" value={snap?.server?.uptime_hours} suffix="h" />
          </div>
        </CardContent></Card>

      <Card><CardHeader className="flex flex-row items-center gap-2"><Database className="w-4 h-4" /><CardTitle>Database</CardTitle></CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-3 gap-3">
            <Stat label="Connections" value={snap?.database?.connections} />
            <Stat label="Slow queries (5m)" value={snap?.database?.slow_queries_5m} />
            <Stat label="Size" value={snap?.database?.size_mb} suffix="MB" />
          </div>
        </CardContent></Card>

      <Card><CardHeader className="flex flex-row items-center gap-2"><ListTree className="w-4 h-4" /><CardTitle>Queues & background jobs</CardTitle></CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-3 gap-3">
            <Stat label="Pending jobs" value={snap?.queues?.jobs_pending} />
            <Stat label="Failed jobs" value={snap?.queues?.jobs_failed} />
            <Stat label="Workers" value={snap?.queues?.workers} />
          </div>
        </CardContent></Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Card><CardHeader className="flex flex-row items-center gap-2"><Bug className="w-4 h-4" /><CardTitle>Errors (24h)</CardTitle></CardHeader><CardContent><div className="font-display text-3xl sm:text-4xl font-black">{snap?.errors_24h ?? "—"}</div></CardContent></Card>
        <Card><CardHeader className="flex flex-row items-center gap-2"><ShieldAlert className="w-4 h-4 text-amber-600" /><CardTitle>Security alerts (24h)</CardTitle></CardHeader><CardContent><div className="font-display text-3xl sm:text-4xl font-black">{snap?.security_alerts ?? "—"}</div></CardContent></Card>
      </div>
      <Badge variant="outline" className="font-mono text-[10px]">CPU / memory / disk / uptime / DB size / queue stats are currently mocked — wire a real Prometheus / OpenTelemetry exporter for live values.</Badge>
    </div>
  );
}
